// outbox.js — the WEB BINDING of the offline write outbox.
//
// The replay ENGINE itself now lives in `@healther/outbox/outbox-engine.js` (W5
// stage 2 of EXPO_REPLATFORM_PLAN.md): it is DOM-free, storage-free and
// framework-free, so Phase 2's Expo app can run the identical algorithm. What
// stays HERE is everything that is genuinely about *this platform*:
//
//   1. WEB_PLATFORM_HOOKS — the browser implementations of the engine's five
//      platform seams (navigator.onLine, the fetch-TypeError network classifier,
//      window CustomEvents, the window 'online' listener, and Web Locks).
//   2. requestStoragePersistence()/getStoragePersistState() — navigator.storage
//      .persist(), a pure browser-eviction concern with no engine involvement.
//   3. class Outbox — the engine pre-bound to those hooks, to the real staff
//      session (getMedplum + NotLoggedInError), so every existing call site and
//      test constructs it exactly as before: `new Outbox({ store })`.
//   4. getOutbox() — the app-wide singleton and the SQLCipher-vs-IndexedDB
//      platform selection.
//
// ONE write path online and offline (REDESIGN_ONLINE_PRIMARY.md): every queued
// write is enqueued the same way; online, we drain immediately so the caller
// still gets a real id synchronously; offline, the row sits 'queued' and the
// 'online' event / interval replays it later. Nothing is ever lost — a row
// leaves the queue only after it lands, or is parked for a human on a real
// conflict/non-retryable error. The full enqueue() terminal-snapshot return
// contract, the idempotent-by-construction replay guarantee, the OutboxStore
// interface and the platform-hook contract are documented in the engine module
// and in packages/outbox/README.md.

import { Outbox as OutboxEngine } from '@healther/outbox/outbox-engine.js';
import { getMedplum as defaultGetMedplum, NotLoggedInError } from './medplum-client.js';
import { isCapacitorNative } from './capacitor-env.js';
import { isNetworkError } from './net-error.js';

// ── Durable-storage persistence (REDESIGN_ONLINE_PRIMARY.md §11C) ─────────────
// The browser may EVICT an origin's IndexedDB under storage pressure. For this
// queue that would mean a SILENT loss of a write captured offline (the exact
// thing the outbox exists to prevent). navigator.storage.persist() asks the
// browser to exempt this origin from eviction. We request it ONCE at outbox init
// on the WEB build (skipped on native/Capacitor — its SQLCipher store is not
// subject to browser eviction), record the granted result, and broadcast it so
// the sync-status UI COULD warn when persistence was DENIED (the queue still
// works; it is just evictable, so an unsynced write is at risk under pressure).
let _persistState = null; // { supported, persisted } once probed, else null

/** The last storage-persistence outcome we proved this session, or null if not
 *  yet probed. Read by the sync-status UI to decide whether to warn (see the
 *  screen-hookup note in the module docs). */
export function getStoragePersistState() { return _persistState; }

/**
 * Ask the browser to make this origin's storage persistent (eviction-exempt) and
 * record + broadcast the outcome. Safe to call where the API is missing (records
 * { supported:false }). Idempotent: if already granted we do NOT re-prompt.
 * Returns the resolved { supported, persisted } state.
 */
export async function requestStoragePersistence() {
  if (typeof navigator === 'undefined' || !navigator.storage
      || typeof navigator.storage.persist !== 'function') {
    _persistState = { supported: false, persisted: false };
  } else {
    let persisted = false;
    try {
      // persisted() first so an already-granted origin doesn't re-prompt.
      if (typeof navigator.storage.persisted === 'function') {
        persisted = await navigator.storage.persisted();
      }
      if (!persisted) persisted = await navigator.storage.persist();
    } catch (_) {
      persisted = false; // API present but threw → treat as denied, never fatal.
    }
    _persistState = { supported: true, persisted: !!persisted };
  }
  if (typeof window !== 'undefined') {
    // Stash on window so a decoupled sync chip can SEED from it without importing
    // this module, and fire an event so a mounted chip updates live.
    try { window.__opdStoragePersist = _persistState; } catch (_) { /* non-DOM host */ }
    try { window.dispatchEvent(new CustomEvent('opd:storage-persist', { detail: _persistState })); } catch (_) { /* non-DOM host */ }
  }
  return _persistState;
}

// ── The browser implementations of the engine's platform hooks ────────────────
// Each is the code the engine used to run inline, moved verbatim behind a name.
// They are read at CALL time, never captured at construction, so a test that
// redefines navigator.onLine after constructing an Outbox still steers it.
export const WEB_PLATFORM_HOOKS = Object.freeze({
  // navigator.onLine is a lying hint (a Capacitor WebView keeps it true in
  // airplane mode; a LAN can be up with the server down) — the engine treats it
  // as exactly that, an optimistic trigger whose failure is absorbed.
  isOnline: () => (typeof navigator === 'undefined' ? true : !!navigator.onLine),

  isNetworkError,

  emit: (name, detail) => {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }
  },

  subscribeOnline: (handler) => {
    if (typeof window === 'undefined') return null;
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  },

  // Web Locks ensure only ONE browser tab drains at a time — a second tab skips
  // its cycle ({ifAvailable:true}) rather than racing updates/backoff. Where
  // navigator.locks is missing (jsdom, the Node harness, an old WebView) we run
  // straight through, which is the original single-context behaviour.
  withDrainLock: (name, run, onUnavailable) => {
    if (typeof navigator === 'undefined' || !navigator.locks
        || typeof navigator.locks.request !== 'function') {
      return run();
    }
    return navigator.locks.request(name, { ifAvailable: true },
      async (lock) => (lock ? run() : onUnavailable()));
  },
});

/**
 * The offline write outbox as this app uses it: the shared engine pre-bound to
 * the browser hooks above and to the real staff session. Construct with a store
 * (from outbox-store.js) and optionally an override getMedplum (the harness
 * injects an in-memory store + a bearer-backed client). All triggers are wired
 * by start().
 */
export class Outbox extends OutboxEngine {
  constructor(options = {}) {
    super({
      getMedplum: defaultGetMedplum,
      hooks: WEB_PLATFORM_HOOKS,
      // A missing session must park the drain quietly (queue intact) and rethrow
      // anything else — the strict typed check, not a name string.
      isNotLoggedIn: (e) => e instanceof NotLoggedInError,
      ...options,
    });
  }
}

// ── The app-wide singleton ────────────────────────────────────────────────────
// One Outbox per browser, lazily constructed against the real IndexedDB store
// and the real staff session. Integration modules (medplum-callin.js,
// medplum-writes.js) enqueue through getOutbox(). The harness bypasses this and
// constructs its own Outbox with an in-memory store + injected client.
let singleton = null;
export async function getOutbox() {
  if (!singleton) {
    // Platform selection: on a native device the durable queue lives in the
    // encrypted SQLite store (SQLCipher); on the web it stays the IndexedDB
    // store, unchanged. Same OutboxStore contract either way, so the engine
    // is identical on both.
    let store;
    if (isCapacitorNative()) {
      const { createSqliteOutboxStore } = await import('./sqlite-store.js');
      store = await createSqliteOutboxStore();
    } else {
      const { createOutboxStore } = await import('./outbox-store.js');
      store = await createOutboxStore();
      // WEB ONLY: ask the browser to make our IndexedDB queue eviction-exempt so
      // an unsynced offline write can't be silently evicted (§11C). Fire-and-
      // forget: a denial is recorded + broadcast (getStoragePersistState) for the
      // UI to warn on, but must never block handing back the singleton. Skipped on
      // native — the SQLite store above is not browser-evictable.
      requestStoragePersistence().catch(() => {});
    }
    singleton = new Outbox({ store }).start();
    // Fire counts ONCE on init so a freshly-mounted sync chip converges to the
    // real queue immediately — without this, a chip that mounts before any
    // enqueue/drain (and, on native, over a SQLite queue it can't read directly)
    // would sit at zeros until the next event. Fire-and-forget: a read failure
    // here must not block handing back the singleton.
    singleton._fireCounts().catch(() => {});
  }
  return singleton;
}
